/*
 * This component COULD be refactored and moved to a shared component library,
 * or even to implements inside of the Popover component (for example, via new subcomponent).
 * Therefore, it was implemented separately to facilitate the future changes.
 *
 * @andersonba
 */

import { Popover, PopoverContentElement, PopoverContentProps, PopoverProps } from '@farol-ds/react'
import {
  forwardRef,
  ReactNode,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'

import styles from './popover-hover.module.scss'

export type PopoverHoverElement = {
  refreshElementNodes: () => void
}

export type PopoverHoverProps<E extends HTMLElement = HTMLElement> = PopoverContentProps & {
  getElementNodes: () => E[] | NodeListOf<E>
  children: ReactNode
  placement?: PopoverProps['placement']
  open?: boolean
  openDelay?: number
  closeDelay?: number
  onOpenChange?: (open: boolean, element: E | null) => void
  triggerOnClick?: boolean
}

function PopoverHoverInner<E extends HTMLElement>(
  props: PopoverHoverProps<E>,
  forwardedRef: Ref<PopoverHoverElement>
) {
  const {
    getElementNodes,
    children,
    openDelay = 700,
    closeDelay = 300,
    open: openProp,
    onOpenChange: onOpenChangeProp,
    placement,
    triggerOnClick = false,
    ...rest
  } = props

  const [current, _setCurrent] = useState<E | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)
  const popoverRef = useRef<PopoverContentElement>(null)
  const posAnchorRef = useRef({ x: 0, y: 0, w: 0 })
  const preventCloseRef = useRef(false)
  const timeoutRef = useRef<NodeJS.Timeout>()

  useImperativeHandle(forwardedRef, () => ({
    refreshElementNodes: () => setRefreshKey(refreshKey + 1),
  }))

  const setCurrent = useCallback(
    (value: E | null) => {
      onOpenChangeProp?.(!!value, value)
      _setCurrent(value)
    },
    [onOpenChangeProp]
  )

  useEffect(() => {
    function bindEvents(): () => void {
      function setAnchorPosition(targetElement: HTMLElement) {
        const { x, width: w, y: elementY, height } = targetElement.getBoundingClientRect()
        const y = elementY + window.scrollY + height // bottom of the element
        posAnchorRef.current = {
          y,
          x,
          w,
        }
      }

      function handleClick(evt: Event) {
        if (evt instanceof MouseEvent && evt.target instanceof HTMLElement) {
          evt.preventDefault()
          const targetElement = evt.target as E
          setAnchorPosition(targetElement)
          setCurrent(targetElement)
        }
      }

      function handleMouseEnter(evt: Event) {
        clearTimeout(timeoutRef.current)
        if (evt instanceof MouseEvent && evt.target instanceof HTMLElement) {
          const targetElement = evt.target as E
          setAnchorPosition(targetElement)
          timeoutRef.current = setTimeout(() => setCurrent(targetElement), openDelay)
        }
      }

      function handleMouseLeave(evt: MouseEvent) {
        if (preventCloseRef.current) {
          preventCloseRef.current = false
          return
        }
        if (
          popoverRef.current &&
          evt.relatedTarget instanceof Element &&
          popoverRef.current.contains(evt.relatedTarget)
        ) {
          return
        }
        clearTimeout(timeoutRef.current)
        timeoutRef.current = setTimeout(() => setCurrent(null), closeDelay)
      }

      function handleContextMenu() {
        clearTimeout(timeoutRef.current)
        preventCloseRef.current = true
      }

      const elements = getElementNodes()

      document.addEventListener('contextmenu', handleContextMenu)
      elements.forEach((el) => {
        el.addEventListener('mouseenter', handleMouseEnter)
        el.addEventListener('mouseleave', handleMouseLeave)

        if (triggerOnClick) {
          el.addEventListener('click', handleClick)
        }
      })

      const cleanup = () => {
        document.removeEventListener('contextmenu', handleContextMenu)
        elements.forEach((el) => {
          el.removeEventListener('mouseenter', handleMouseEnter)
          el.removeEventListener('mouseleave', handleMouseLeave)

          if (triggerOnClick) {
            el.removeEventListener('click', handleClick)
          }
        })
      }

      return cleanup
    }

    return bindEvents()
  }, [getElementNodes, closeDelay, openDelay, setCurrent, refreshKey, triggerOnClick])

  function onOpenChange(open: boolean) {
    if (open) return
    setCurrent(null)
    clearTimeout(timeoutRef.current)
  }

  function onContentMouseEnter(evt: React.MouseEvent<PopoverContentElement>) {
    rest.onMouseEnter?.(evt)
    clearTimeout(timeoutRef.current)
  }

  function onContentMouseLeave(evt: React.MouseEvent<PopoverContentElement>) {
    rest.onMouseLeave?.(evt)
    if (preventCloseRef.current) {
      preventCloseRef.current = false
      return
    }
    clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(() => setCurrent(null), closeDelay)
  }

  // cleanup
  useEffect(() => {
    return () => clearTimeout(timeoutRef.current)
  }, [])

  const open = openProp ?? !!current

  return (
    <Popover onOpenChange={onOpenChange} open={open} placement={placement}>
      {open && (
        <Popover.Anchor asChild>
          <span
            className={styles.anchor}
            style={{
              top: posAnchorRef.current.y,
              left: posAnchorRef.current.x,
              width: posAnchorRef.current.w,
            }}
            data-testid="popover-hover-anchor"
          />
        </Popover.Anchor>
      )}

      <Popover.Content
        data-testid="popover-hover-content"
        {...rest}
        onMouseEnter={onContentMouseEnter}
        onMouseLeave={onContentMouseLeave}
        onInteractOutside={(evt) => {
          props.onInteractOutside?.(evt)

          const { originalEvent } = evt.detail

          // prevent close if the related target is inside the popover
          if (
            popoverRef.current &&
            originalEvent instanceof FocusEvent &&
            originalEvent.relatedTarget instanceof Element &&
            popoverRef.current.contains(originalEvent.relatedTarget)
          ) {
            evt.preventDefault()
          }
        }}
        ref={popoverRef}
      >
        {children}
      </Popover.Content>
    </Popover>
  )
}

export const PopoverHover = forwardRef(PopoverHoverInner) as <E extends HTMLElement>(
  props: PopoverHoverProps<E> & { ref?: React.ForwardedRef<PopoverHoverElement> }
) => ReturnType<typeof PopoverHoverInner>
